import { useProjectObjectQuery, useSuperObjectClassListQuery } from '@/hook';
import { Info, VFlex, VStack } from '@/package';
import type { Project } from '@/type';

import {
  SelectablePieChart,
  calculateExtendClassData,
  calculateExtendClassDataWithBaseClass,
  calculateTotalClassData,
  filterUnknownClass,
} from '../Statistic';
import { filterSuperClassList } from '../Statistic';
import { StaticPieChart } from '../Statistic/StaticPieChart';
import { StatisticTable } from '../Statistic/StatisticTable';
import { useSelectedIndex } from '../Statistic/hook';

export const ProjectTotalLabeledObject = ({
  project,
}: {
  project: Project;
}) => {
  const { selectedIndex, toggleSelect, clearSelection } = useSelectedIndex();
  const { data: projectObject } = useProjectObjectQuery(project.id, true);
  const { data: allSuperClassList } = useSuperObjectClassListQuery();

  const toShowSuperClassList = filterSuperClassList(
    allSuperClassList?.filter(filterUnknownClass),
    project.extendClass,
  );

  const projectTotalData = calculateTotalClassData(
    toShowSuperClassList,
    projectObject?.statExtendClass,
  );

  const extendClassData =
    typeof selectedIndex === 'number'
      ? calculateExtendClassData(
          toShowSuperClassList.at(selectedIndex)?.extendClass,
          projectObject?.statExtendClass,
        )
      : [];

  const extendClassDataWithBaseClass =
    typeof selectedIndex === 'number' && projectTotalData[selectedIndex]
      ? calculateExtendClassDataWithBaseClass(
          projectTotalData[selectedIndex],
          extendClassData,
        )
      : [];

  return (
    <VStack gap={0.5} className="w-full">
      <VFlex className="w-full" justify="between" items="center">
        <div className="text-headline-md font-bold text-blue-300">
          Total labeled objects
        </div>
        <VFlex items="center" justify="end" gap={0.25} className="flex-1">
          <Info className="size-4 text-info-500" />
          <div className="text-body-md">
            Clicking a pie chart slice or table item in the base class also
            allows you to view statistics for its extension classes.
          </div>
        </VFlex>
      </VFlex>
      <VFlex gap={2} items="center" justify="center">
        <SelectablePieChart
          pieData={projectTotalData}
          selectedValue={selectedIndex}
          onSelect={toggleSelect}
        />
        <StatisticTable
          data={projectTotalData}
          selectedValue={selectedIndex}
          onRowClick={toggleSelect}
        />
      </VFlex>

      {typeof selectedIndex === 'number' && extendClassData.length > 0 && (
        <VFlex gap={2} items="center" justify="center">
          <StaticPieChart
            pieData={extendClassDataWithBaseClass}
            onClose={clearSelection}
          />
          <StatisticTable data={extendClassDataWithBaseClass} />
        </VFlex>
      )}
    </VStack>
  );
};

ProjectTotalLabeledObject.displayName = 'ProjectTotalLabeledObject';
